import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import type { NoticeSentResponse } from '@/bindings/NoticeSentResponse'
import type { NoticeTemplate } from '@/bindings/NoticeTemplate'
import type { RestaurantDetail } from '@/bindings/RestaurantDetail'
import type { SendNoticeRequest } from '@/bindings/SendNoticeRequest'
import { ConfirmButton } from '@/components/confirm'
import { Field, Input, Select } from '@/components/field'
import { Card } from '@/components/layout'
import { restaurantPath } from '@/features/restaurants/api'
import { request } from '@/lib/api'
import { useT } from '@/lib/i18n/translate'

/** The templates a platform user sends by hand; deactivated / reactivated come from the activity buttons. */
export type ManualNotice = Exclude<NoticeTemplate['template'], 'deactivated' | 'reactivated'>
const MANUAL_NOTICES: readonly ManualNotice[] = ['payment_reminder', 'deactivation_warning', 'payment_received', 'removal_notice']
/** The notices that must reach someone who is not logging in (decision 6, 2026-09-28). */
const EMAILED: readonly ManualNotice[] = ['deactivation_warning', 'removal_notice']

/** Longest amount the backend accepts, e.g. "300,000 VND". */
const AMOUNT_MAX = 40

/**
 * K3: a templated notice to every manager and superuser of the restaurant. Templates, not free
 * text, so the notice renders in the reader's language; the amount is typed text, no math.
 */
export function NoticesCard({ restaurant }: { restaurant: RestaurantDetail }) {
  const t = useT()
  const [template, setTemplate] = useState<ManualNotice>('payment_reminder')
  const [amount, setAmount] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [deactivateOn, setDeactivateOn] = useState('')
  const [removeOn, setRemoveOn] = useState('')
  const [sent, setSent] = useState<NoticeSentResponse>()

  const send = useMutation({
    mutationFn: (body: SendNoticeRequest) => request<NoticeSentResponse>(restaurantPath(restaurant.id, '/notices'), { method: 'POST', body }),
    onSuccess: setSent,
  })

  function choose(next: ManualNotice) {
    setTemplate(next)
    setSent(undefined)
    send.reset()
  }

  // Complete when every field the template needs is filled; the button waits otherwise.
  const trimmed = amount.trim()
  const draft = ((): NoticeTemplate | undefined => {
    switch (template) {
      case 'payment_reminder':
        return trimmed && dueDate ? { template, amount: trimmed, due_date: dueDate } : undefined
      case 'deactivation_warning':
        return dueDate && deactivateOn ? { template, due_date: dueDate, deactivate_on: deactivateOn } : undefined
      case 'payment_received':
        return trimmed ? { template, amount: trimmed } : undefined
      case 'removal_notice':
        return removeOn ? { template, remove_on: removeOn } : undefined
    }
  })()
  const needsAmount = template === 'payment_reminder' || template === 'payment_received'
  const needsDueDate = template === 'payment_reminder' || template === 'deactivation_warning'

  return (
    <Card className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">{t('restaurants.notices')}</h2>
      <p className="text-muted text-sm">{t('restaurants.noticeHint')}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('restaurants.noticeTemplate')}>
          <Select value={template} onChange={(e) => choose(e.target.value as ManualNotice)}>
            {MANUAL_NOTICES.map((name) => (
              <option key={name} value={name}>
                {t(`notice.template.${name}`)}
              </option>
            ))}
          </Select>
        </Field>
        {needsAmount && (
          <Field label={t('restaurants.noticeAmount')} hint={t('restaurants.noticeAmountHint')}>
            <Input value={amount} maxLength={AMOUNT_MAX} onChange={(e) => setAmount(e.target.value)} />
          </Field>
        )}
        {needsDueDate && (
          <Field label={t('restaurants.noticeDueDate')}>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
        )}
        {template === 'deactivation_warning' && (
          <Field label={t('restaurants.noticeDeactivateOn')}>
            <Input type="date" value={deactivateOn} onChange={(e) => setDeactivateOn(e.target.value)} />
          </Field>
        )}
        {template === 'removal_notice' && (
          <Field label={t('restaurants.noticeRemoveOn')}>
            <Input type="date" value={removeOn} onChange={(e) => setRemoveOn(e.target.value)} />
          </Field>
        )}
      </div>
      {sent && (
        <p role="status" className="text-muted text-sm">
          {t('restaurants.noticeSent', { recipients: sent.recipients, emails: sent.emails })}
        </p>
      )}
      <div className="flex justify-end">
        <ConfirmButton
          label={t('restaurants.sendNotice')}
          variant="secondary"
          disabled={!draft}
          confirmLabel={t('restaurants.confirmSend')}
          confirmVariant="primary"
          align="end"
          note={
            <p className="text-muted text-sm">
              {t(EMAILED.includes(template) ? 'restaurants.noticeGoesToEmail' : 'restaurants.noticeGoesTo', { name: restaurant.name })}
            </p>
          }
          confirm={() => (draft ? send.mutateAsync({ template: draft }) : Promise.resolve())}
          error={send.error}
        />
      </div>
    </Card>
  )
}
